/** Top-level browse group on /vendors (order = tab order on directory). */
export const DIRECTORY_SECTORS = ['shop', 'beauty', 'services', 'pros', 'property'] as const;
export type DirectorySector = (typeof DIRECTORY_SECTORS)[number];

export const DIRECTORY_SECTOR_LABELS: Record<DirectorySector, string> = {
  shop: 'Shop',
  beauty: 'Beauty & Health',
  services: 'Services',
  pros: 'Pros',
  property: 'Property',
};

export const DIRECTORY_SECTOR_HINTS: Record<DirectorySector, string> = {
  shop: 'Products & local stores',
  beauty: 'Salons, barbers, nails & spa',
  services: 'Rentals, events, transport & creative',
  pros: 'Trades, repairs & quotes',
  property: 'Real estate agencies & listings',
};

export type DirectoryCategoryDef = {
  id: string;
  sector: DirectorySector;
  label: string;
};

/** Subcategories within each sector (stable ids stored on Vendor.directoryCategoryId). */
export const DIRECTORY_CATEGORIES: DirectoryCategoryDef[] = [
  { id: 'shop-retail', sector: 'shop', label: 'Retail & general' },
  { id: 'shop-food', sector: 'shop', label: 'Food & beverages' },
  { id: 'shop-fashion', sector: 'shop', label: 'Fashion & clothing' },
  { id: 'shop-electronics', sector: 'shop', label: 'Electronics & tech' },
  { id: 'shop-home', sector: 'shop', label: 'Home & kitchen' },
  { id: 'shop-agriculture', sector: 'shop', label: 'Agriculture & farm' },
  { id: 'shop-crafts', sector: 'shop', label: 'Arts, crafts & gifts' },
  { id: 'shop-other', sector: 'shop', label: 'Other shops' },

  { id: 'beauty-hair-salon', sector: 'beauty', label: 'Hair salons' },
  { id: 'beauty-barber', sector: 'beauty', label: 'Barbers' },
  { id: 'beauty-nail', sector: 'beauty', label: 'Nail bars' },
  { id: 'beauty-spa', sector: 'beauty', label: 'Spa & wellness' },
  { id: 'beauty-skincare', sector: 'beauty', label: 'Skincare & aesthetics' },
  { id: 'beauty-makeup', sector: 'beauty', label: 'Makeup & lashes' },
  { id: 'beauty-fitness', sector: 'beauty', label: 'Fitness & wellness' },
  { id: 'beauty-other', sector: 'beauty', label: 'Other beauty' },

  { id: 'svc-photography', sector: 'services', label: 'Photographers' },
  { id: 'svc-videography', sector: 'services', label: 'Videographers' },
  { id: 'svc-events', sector: 'services', label: 'Event planning' },
  { id: 'svc-catering', sector: 'services', label: 'Catering' },
  { id: 'svc-entertainment', sector: 'services', label: 'DJs & entertainment' },
  { id: 'svc-print-design', sector: 'services', label: 'Printing & design' },
  { id: 'svc-tutoring', sector: 'services', label: 'Tutoring & lessons' },
  { id: 'svc-marketing', sector: 'services', label: 'Marketing & content' },
  { id: 'svc-rental', sector: 'services', label: 'Car & vehicle rental' },
  { id: 'svc-transport', sector: 'services', label: 'Transport & shuttles' },
  { id: 'svc-tours', sector: 'services', label: 'Tours & experiences' },
  { id: 'svc-equipment', sector: 'services', label: 'Equipment rental' },
  { id: 'svc-professional', sector: 'services', label: 'Professional services' },
  { id: 'svc-other', sector: 'services', label: 'Other services' },

  { id: 'pro-electrical', sector: 'pros', label: 'Electrical' },
  { id: 'pro-solar', sector: 'pros', label: 'Solar & renewable energy' },
  { id: 'pro-plumbing', sector: 'pros', label: 'Plumbing' },
  { id: 'pro-ac', sector: 'pros', label: 'AC & cooling' },
  { id: 'pro-carpentry', sector: 'pros', label: 'Carpentry & building' },
  { id: 'pro-landscaping', sector: 'pros', label: 'Landscaping' },
  { id: 'pro-cleaning', sector: 'pros', label: 'Cleaning' },
  { id: 'pro-auto', sector: 'pros', label: 'Automotive' },
  { id: 'pro-other', sector: 'pros', label: 'Other services' },

  { id: 'prop-agency', sector: 'property', label: 'Real estate agencies' },
  { id: 'prop-management', sector: 'property', label: 'Property management' },
  { id: 'prop-other', sector: 'property', label: 'Other property services' },
];

export const DIRECTORY_CATEGORY_BY_ID = Object.fromEntries(
  DIRECTORY_CATEGORIES.map((c) => [c.id, c]),
) as Record<string, DirectoryCategoryDef>;

export function directoryCategoriesForSector(sector: DirectorySector): DirectoryCategoryDef[] {
  return DIRECTORY_CATEGORIES.filter((c) => c.sector === sector);
}

/** Legacy Vendor.businessType → directory category id */
export const BUSINESS_TYPE_TO_DIRECTORY_CATEGORY: Record<string, string> = {
  retail: 'shop-retail',
  food: 'shop-food',
  fashion: 'shop-fashion',
  electronics: 'shop-electronics',
  home: 'shop-home',
  agriculture: 'shop-agriculture',
  crafts: 'shop-crafts',
  beauty: 'beauty-other',
  salon: 'beauty-hair-salon',
  barber: 'beauty-barber',
  spa: 'beauty-spa',
  trades: 'pro-other',
  real_estate: 'prop-agency',
  photography: 'svc-photography',
  videography: 'svc-videography',
  events: 'svc-events',
  catering: 'svc-catering',
  creative: 'svc-print-design',
  rental: 'svc-rental',
  transport: 'svc-transport',
  other: 'shop-other',
};

/** Directory category → legacy businessType (onboarding / reports). */
export const DIRECTORY_CATEGORY_TO_BUSINESS_TYPE: Record<string, string> = {
  'shop-retail': 'retail',
  'shop-food': 'food',
  'shop-fashion': 'fashion',
  'shop-electronics': 'electronics',
  'shop-home': 'home',
  'shop-agriculture': 'agriculture',
  'shop-crafts': 'crafts',
  'shop-other': 'other',
  'beauty-hair-salon': 'salon',
  'beauty-barber': 'barber',
  'beauty-nail': 'beauty',
  'beauty-spa': 'spa',
  'beauty-skincare': 'beauty',
  'beauty-makeup': 'beauty',
  'beauty-fitness': 'beauty',
  'beauty-other': 'beauty',
  'svc-photography': 'other',
  'svc-videography': 'other',
  'svc-events': 'other',
  'svc-catering': 'food',
  'svc-entertainment': 'other',
  'svc-print-design': 'crafts',
  'svc-tutoring': 'other',
  'svc-marketing': 'other',
  'svc-rental': 'other',
  'svc-transport': 'other',
  'svc-tours': 'other',
  'svc-equipment': 'other',
  'svc-professional': 'other',
  'svc-other': 'other',
  'pro-electrical': 'trades',
  'pro-solar': 'trades',
  'pro-plumbing': 'trades',
  'pro-ac': 'trades',
  'pro-carpentry': 'trades',
  'pro-landscaping': 'trades',
  'pro-cleaning': 'trades',
  'pro-auto': 'trades',
  'pro-other': 'trades',
  'prop-agency': 'real_estate',
  'prop-management': 'real_estate',
  'prop-other': 'real_estate',
};

/** ServiceProvider.categories (cat-pro-*) → directory category */
export const PRO_CATEGORY_TO_DIRECTORY: Record<string, string> = {
  'cat-pro-elec': 'pro-electrical',
  'cat-pro-plumb': 'pro-plumbing',
  'cat-pro-ac': 'pro-ac',
  'cat-pro-carpentry': 'pro-carpentry',
  'cat-pro-landscape': 'pro-landscaping',
  'cat-pro-cleaning': 'pro-cleaning',
  'cat-pro-auto': 'pro-auto',
  'cat-pro-salon': 'beauty-hair-salon',
  'cat-pro-barber': 'beauty-barber',
  'cat-pro-nail': 'beauty-nail',
  'cat-pro-spa': 'beauty-spa',
};

export function directoryCategoryLabel(categoryId?: string): string | undefined {
  if (!categoryId) return undefined;
  return DIRECTORY_CATEGORY_BY_ID[categoryId]?.label ?? categoryId;
}

/** Readable spacing for custom directory labels (camelCase, &). */
export function normalizeDirectoryCategoryCustomLabel(raw: string): string {
  let label = raw.trim();
  if (!label) return '';
  label = label.replace(/([a-z])([A-Z])/g, '$1 $2');
  label = label.replace(/\s*&\s*/g, ' & ');
  return label.replace(/\s+/g, ' ').trim();
}

/** Public label on directory cards — custom override, then taxonomy label. */
export function directoryCategoryDisplayLabel(
  categoryId?: string,
  customLabel?: string | null,
): string | undefined {
  const custom = customLabel?.trim();
  if (custom) return normalizeDirectoryCategoryCustomLabel(custom);
  return directoryCategoryLabel(categoryId);
}

export const DIRECTORY_OTHER_CATEGORY_IDS = new Set([
  'shop-other',
  'beauty-other',
  'svc-other',
  'pro-other',
  'prop-other',
]);

export function isDirectoryOtherCategory(categoryId?: string): boolean {
  return !!categoryId && DIRECTORY_OTHER_CATEGORY_IDS.has(categoryId);
}

export function directorySectorFromCategoryId(categoryId: string): DirectorySector | undefined {
  return DIRECTORY_CATEGORY_BY_ID[categoryId]?.sector;
}

export function inferDirectoryCategoryId(
  businessType?: string,
  proCategoryIds?: string[],
): string {
  if (proCategoryIds?.length) {
    for (const id of proCategoryIds) {
      const mapped = PRO_CATEGORY_TO_DIRECTORY[id];
      if (mapped) return mapped;
    }
  }
  if (businessType) {
    const mapped = BUSINESS_TYPE_TO_DIRECTORY_CATEGORY[businessType.toLowerCase()];
    if (mapped) return mapped;
  }
  return 'shop-other';
}

export function resolveDirectoryClassification(input: {
  directorySector?: string;
  directoryCategoryId?: string;
  businessType?: string;
  proCategoryIds?: string[];
  primaryMode?: string;
}): { sector: DirectorySector; categoryId: string } {
  if (
    input.directorySector &&
    DIRECTORY_SECTORS.includes(input.directorySector as DirectorySector) &&
    input.directoryCategoryId &&
    DIRECTORY_CATEGORY_BY_ID[input.directoryCategoryId]
  ) {
    return {
      sector: input.directorySector as DirectorySector,
      categoryId: input.directoryCategoryId,
    };
  }
  const categoryId = input.directoryCategoryId && DIRECTORY_CATEGORY_BY_ID[input.directoryCategoryId]
    ? input.directoryCategoryId
    : inferDirectoryCategoryId(input.businessType, input.proCategoryIds);
  const sectorFromCategory = directorySectorFromCategoryId(categoryId);
  const sector =
    input.directorySector && DIRECTORY_SECTORS.includes(input.directorySector as DirectorySector)
      ? (input.directorySector as DirectorySector)
      : (sectorFromCategory ??
        (input.primaryMode === 'listings'
          ? 'property'
          : input.primaryMode === 'book'
            ? 'beauty'
            : input.primaryMode === 'hire'
              ? 'pros'
              : 'shop'));
  return { sector, categoryId };
}

/** Categories that default to bookable appointments (rentals, transport, beauty, etc.). */
export const DIRECTORY_BOOKING_CATEGORY_IDS = new Set([
  'svc-rental',
  'svc-transport',
  'svc-tours',
  'svc-equipment',
  'beauty-hair-salon',
  'beauty-barber',
  'beauty-nail',
  'beauty-spa',
  'beauty-skincare',
  'beauty-makeup',
  'beauty-fitness',
]);
